import { useEffect, useRef, useState } from 'react';
import { ChevronUp, Check } from 'lucide-react';

// A pop-up picker for the V3 control bar (used for the branch on Transport). The list is drawn inside this component, not in a
// portal, directly above the button it belongs to, so it always opens exactly where the button is, at any page zoom, and
// slides up from it. Same choices and same result as the dropdown it replaces: onChange gets the chosen value.
const V3PopUpSelect = ({ value, options, onChange, testId, ariaLabel }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => { if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const current = options.find((option) => option.value === value);

  return (
    <div className="v3-picker" ref={wrapRef}>
      <button
        type="button"
        className={`v3-picker-trigger ${open ? 'v3-picker-trigger--open' : ''}`}
        onClick={() => setOpen((previous) => !previous)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        data-testid={testId}
      >
        <span>{current ? current.label : value}</span>
        <ChevronUp className="v3-i v3-i--sm v3-picker-chevron" />
      </button>
      {open && (
        <div className="v3-picker-menu" role="listbox" aria-label={ariaLabel} data-testid={`${testId}-menu`}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={`v3-picker-option ${option.value === value ? 'v3-picker-option--on' : ''}`}
              onClick={() => { setOpen(false); if (option.value !== value) onChange(option.value); }}
              data-testid={`${testId}-option-${option.value}`}
            >
              <span>{option.label}</span>
              {option.value === value && <Check className="v3-i v3-i--sm" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default V3PopUpSelect;
